import { lazy, Suspense } from "react";

/**
 * Page-wide globe backdrop. Fixed to the viewport behind all home sections so
 * the hero, stats, timelines, and CTA share one continuous 3D background.
 *
 * Layering notes:
 *  - `fixed inset-0`: viewport-anchored, scrolls with nothing
 *  - `z-0`: below the content layer (which sits at `z-10` in Home.tsx)
 *  - `pointer-events-none`: users interact with content, not the globe
 *
 * The globe itself is heavy so it's lazy-loaded behind Suspense. While it
 * loads we show a CSS-only starfield poster so the viewport never appears
 * as an empty black void during the WebGL bootstrap window.
 */
const HeroScene = lazy(() => import("./HeroScene"));

/**
 * Static poster that stands in for the globe until it's ready. Pure CSS so
 * it's rendered the moment the prerendered HTML paints — no layout shift,
 * no JS cost, no decode time.
 */
const StarfieldPoster = () => (
    <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
            backgroundColor: "hsl(240 33% 4%)",
            backgroundImage: [
                // Soft purple glow where the globe will appear
                "radial-gradient(ellipse 60% 50% at 50% 48%, hsl(260 60% 18% / 0.85) 0%, transparent 60%)",
                // Cyan rim highlight
                "radial-gradient(ellipse 35% 28% at 50% 45%, hsl(190 80% 20% / 0.45) 0%, transparent 65%)",
                // Star dots (layered radial-gradient pattern)
                "radial-gradient(1px 1px at 20% 30%, hsl(0 0% 100% / 0.55) 50%, transparent 100%)",
                "radial-gradient(1px 1px at 70% 60%, hsl(0 0% 100% / 0.5) 50%, transparent 100%)",
                "radial-gradient(1.5px 1.5px at 40% 80%, hsl(0 0% 100% / 0.4) 50%, transparent 100%)",
                "radial-gradient(1px 1px at 85% 20%, hsl(0 0% 100% / 0.45) 50%, transparent 100%)",
                "radial-gradient(1px 1px at 15% 70%, hsl(0 0% 100% / 0.4) 50%, transparent 100%)",
                "radial-gradient(2px 2px at 55% 15%, hsl(0 0% 100% / 0.3) 50%, transparent 100%)",
                "radial-gradient(1px 1px at 90% 85%, hsl(0 0% 100% / 0.35) 50%, transparent 100%)",
            ].join(","),
        }}
    />
);

const HomeBackdrop = () => (
    <div
        aria-hidden
        className="fixed inset-0 z-0 pointer-events-none"
        style={{ contain: "strict" }}
    >
        <Suspense fallback={<StarfieldPoster />}>
            <HeroScene />
        </Suspense>

        {/* Uniform veil across the viewport so text stays legible over every
            section — no per-section vignette that would create a visible seam
            between the hero and the content below it. Slightly denser at the
            top + bottom so navbar and footer retain contrast. */}
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    "linear-gradient(180deg, hsl(240 33% 4% / 0.55) 0%, hsl(240 33% 4% / 0.28) 12%, hsl(240 33% 4% / 0.28) 88%, hsl(240 33% 4% / 0.7) 100%)",
            }}
        />
    </div>
);

export default HomeBackdrop;
